import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { CTAS, SITE, whatsappHref } from "@/content/site";
import { Reveal } from "@/lib/motion";

interface CtaLinkProps {
  href: string;
  children: ReactNode;
  variant?: "primary" | "secondary";
  testId: string;
  className?: string;
  external?: boolean;
}

export function CtaLink({ href, children, variant = "primary", testId, className = "", external }: CtaLinkProps) {
  const cls = variant === "primary" ? "btn-primary" : "btn-secondary";
  const externalProps = external ? { target: "_blank", rel: "noopener noreferrer" } : {};
  if (href.startsWith("/")) {
    return (
      <Link to={href} className={`${cls} ${className}`} data-testid={testId} {...externalProps}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={`${cls} ${className}`} data-testid={testId} {...externalProps}>
      {children}
    </a>
  );
}

interface CtaBlockProps {
  titulo?: string;
  sub?: string;
  primaryLabel?: string;
  primaryHref?: string;
  secondary?: boolean;
  note?: string;
}

export function CtaBlock({
  titulo = "Cuéntanos tu situación",
  sub = "Describe qué está pasando y te decimos cómo seguir: clara, sin compromiso y sin tecnicismos.",
  primaryLabel,
  primaryHref,
  secondary = true,
  note = SITE.disponibilidad,
}: CtaBlockProps) {
  return (
    <section className="border-t border-hairline bg-ink-deep" data-testid="cta-block">
      <div className="mx-auto max-w-7xl px-4 py-20 text-center sm:px-8 sm:py-28">
        <Reveal>
          <p className="eyebrow mb-6 text-salvia-marco">El siguiente paso</p>
          <h2 className="mx-auto max-w-3xl font-serif text-4xl leading-[1.1] text-parchment sm:text-5xl lg:text-6xl">
            {titulo}
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-parchment/70">{sub}</p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <CtaLink
              href={primaryHref || CTAS.principal.href}
              testId="cta-block-principal"
              variant="primary"
            >
              {primaryLabel || CTAS.principal.label}
              <ArrowRight className="h-4 w-4" />
            </CtaLink>
            {secondary && (
              <CtaLink href={whatsappHref()} testId="cta-block-secundaria" variant="secondary" className="border-parchment/40 text-parchment hover:bg-parchment hover:text-ink-deep" external={!!SITE.WHATSAPP_NUMERO}>
                {CTAS.secundaria.label}
                <ArrowUpRight className="h-4 w-4" />
              </CtaLink>
            )}
          </div>
          {note && <p className="mt-8 font-mono text-xs tracking-wide text-parchment/50">{note}</p>}
        </Reveal>
      </div>
    </section>
  );
}
